import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { api } from "./axiosInstance";
import { ClipboardDocumentIcon, Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const Header = () => {
    const [decoded, setDecoded] = useState(null);
    const [userId, setUserId] = useState("");
    const referralLink = userId ? `https://careermission.ai/candidate-signup?referredBy=${userId}` : "";
    const [userRole, setUserRole] = useState("");
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [company, setCompany] = useState(null);
    const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
    const [isInviteModalOpen, setInviteModalOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [employerDropdownOpen, setEmployerDropdownOpen] = useState(false);
    const [candidateDropdownOpen, setCandidateDropdownOpen] = useState(false);
    const [inviteData, setInviteData] = useState({
        firstName: "",
        lastName: "",
        email: ""
    });
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const impersonatedUser = localStorage.getItem("impersonatedUser");
        let tokenData = null;
        if (impersonatedUser) {
            try {
                tokenData = JSON.parse(impersonatedUser);
            } catch (err) {
                console.error("❌ Error parsing impersonated user:", err);
            }
        } else {
            const token = localStorage.getItem("token");
            if (token) {
                try {
                    tokenData = jwtDecode(token);
                } catch (err) {
                    console.error("❌ Error decoding token:", err);
                }
            }
        }
        setDecoded(tokenData);
        if (tokenData) {
            setUserId(tokenData.userId || "");
            setUserRole((tokenData.role || "").toLowerCase());
            setIsSuperUser(tokenData.isSuperUser || false);
            setCompany(tokenData.company || null);

            // FIX: Get token and attach to request
            const storedToken = localStorage.getItem("token");

            api.get(`/auth/user/${tokenData.userId}`, {
                headers: { Authorization: `Bearer ${storedToken}` }
            })
                .then(response => {
                    const userData = response.data;
                    setAllQuestionsAnswered(userData.allQuestionsAnswered);
                })
                .catch(err => {
                    // Optional: Don't log out on this specific error, just log it
                    console.error("❌ Error fetching user data:", err);
                });
        }
    }, [location]);

    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/");
    };

    const openInviteModal = () => {
        setInviteModalOpen(true);
    };

    const closeInviteModal = () => {
        setInviteModalOpen(false);
        setInviteData({ firstName: "", lastName: "", email: "" });
    };

    const handleInviteChange = (e) => {
        const { name, value } = e.target;
        setInviteData(prev => ({ ...prev, [name]: value }));
    };

    // Updated sendInvite to include the token in the Authorization header
    const sendInvite = async () => {
        try {
            const token = localStorage.getItem("token");
            await api.post("/auth/invite-candidate", inviteData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Invitation sent successfully!");
            closeInviteModal();
        } catch (error) {
            console.error("Error sending invite:", error);

            // Extract and display the error message if available
            if (error.response && error.response.data && error.response.data.error) {
                alert(error.response.data.error);
            } else {
                alert("An unexpected error occurred.");
            }
        }
    };

    const isLoggedIn = !!decoded;

    return (
        <nav className="bg-bluegray p-4 flex justify-between items-center shadow-md fixed w-full z-10">
            {/* Left Side: Logo and Invite Button */}
            <div className="flex items-center space-x-4">
                <Link to="/" className="w-12 h-12">
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 250 220"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M 120,140 L 120,60 L 150,100 L 180,60 L 180,140"
                            stroke="#A2D4F2"
                            strokeWidth="14"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M 120,60 A 50,50 0 1,0 120,140"
                            stroke="#FF8303"
                            strokeWidth="14"
                            fill="none"
                            strokeLinecap="round"
                        />
                    </svg>
                </Link>
                {isLoggedIn && (
                    <button
                        onClick={openInviteModal}
                        className="px-4 py-2 bg-orange text-white font-medium rounded hover:bg-darkblue"
                    >
                        Invite a Friend
                    </button>
                )}
            </div>
            {/* Hamburger Button (Visible on Small Screens) */}
            <button
                className="lg:hidden p-2 text-white"
                onClick={() => { setMenuOpen(!menuOpen); setEmployerDropdownOpen(false); setCandidateDropdownOpen(false); }}
            >
                {menuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
            </button>

            {/* Center Navigation Tabs */}
            {isLoggedIn && (
                <div className={`lg:flex flex-grow justify-center space-x-6 ${menuOpen ? "block absolute top-16 left-0 w-full bg-bluegray p-4 flex flex-col space-y-4" : "hidden lg:flex"}`}>
                    <Link to="/profile" className="px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)}>Profile</Link>
                    <Link to="/assessment" className="px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)}>Assessment</Link>
                    {allQuestionsAnswered && <Link to="/matches" className="px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)}>Matches</Link>}
                    {allQuestionsAnswered && <Link to="/results" className="px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)}>Results</Link>}
                    {userRole !== "candidate" && company && <Link to="/employer-dashboard" className="px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)}>Employer Dashboard</Link>}
                    {isSuperUser && <Link to="/admin" className="px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)}>SuperUser Dashboard</Link>}
                </div>
            )}

            {/* Logged-out navigation with dropdowns */}
            {!isLoggedIn && (
              <>
                {/* Desktop logged-out nav */}
                <div className="hidden lg:flex flex-grow justify-center items-center space-x-6">
                  {/* For Employers dropdown */}
                  <div className="relative group">
                    <button className="flex items-center px-4 py-2 text-white hover:bg-deepblue" aria-haspopup="true" aria-expanded="false">
                      For Employers
                      <ChevronDownIcon className="w-4 h-4 ml-1" />
                    </button>
                    <div className="hidden group-hover:block absolute top-full left-0 bg-bluegray shadow-lg rounded-b-lg py-2 min-w-48 z-20" role="menu">
                      <Link to="/employer-how-it-works" className="block px-4 py-2 text-white hover:bg-deepblue text-base" role="menuitem">How It Works</Link>
                      <Link to="/how-to-pick-great-targets" className="block px-4 py-2 text-white hover:bg-deepblue text-base" role="menuitem">How to Pick Great Targets</Link>
                      <Link to="/pricing" className="block px-4 py-2 text-white hover:bg-deepblue text-base" role="menuitem">Pricing</Link>
                      <Link to="/guides" className="block px-4 py-2 text-white hover:bg-deepblue text-base" role="menuitem">Guides</Link>
                    </div>
                  </div>
                  {/* For Candidates dropdown */}
                  <div className="relative group">
                    <button className="flex items-center px-4 py-2 text-white hover:bg-deepblue" aria-haspopup="true" aria-expanded="false">
                      For Candidates
                      <ChevronDownIcon className="w-4 h-4 ml-1" />
                    </button>
                    <div className="hidden group-hover:block absolute top-full left-0 bg-bluegray shadow-lg rounded-b-lg py-2 min-w-48 z-20" role="menu">
                      <Link to="/candidate-how-it-works" className="block px-4 py-2 text-white hover:bg-deepblue text-base" role="menuitem">How It Works</Link>
                      <Link to="/guides" className="block px-4 py-2 text-white hover:bg-deepblue text-base" role="menuitem">Guides</Link>
                    </div>
                  </div>
                  {/* Standalone Pricing link */}
                  <Link to="/pricing" className="px-4 py-2 text-white font-bold hover:bg-deepblue">Pricing</Link>
                </div>

                {/* Mobile logged-out hamburger content */}
                {menuOpen && (
                  <div className="lg:hidden absolute top-16 left-0 w-full bg-bluegray p-4 flex flex-col space-y-2 z-20">
                    {/* For Employers collapsible */}
                    <button
                      className="flex items-center justify-between px-4 py-2 text-white w-full"
                      onClick={() => setEmployerDropdownOpen(!employerDropdownOpen)}
                      aria-haspopup="true"
                      aria-expanded={employerDropdownOpen}
                    >
                      For Employers
                      <ChevronDownIcon className={`w-4 h-4 transition-transform ${employerDropdownOpen ? "rotate-180" : ""}`} />
                    </button>
                    {employerDropdownOpen && (
                      <div className="pl-4 flex flex-col space-y-1" role="menu">
                        <Link to="/employer-how-it-works" className="block px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)} role="menuitem">How It Works</Link>
                        <Link to="/how-to-pick-great-targets" className="block px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)} role="menuitem">How to Pick Great Targets</Link>
                        <Link to="/pricing" className="block px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)} role="menuitem">Pricing</Link>
                        <Link to="/guides" className="block px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)} role="menuitem">Guides</Link>
                      </div>
                    )}
                    {/* For Candidates collapsible */}
                    <button
                      className="flex items-center justify-between px-4 py-2 text-white w-full"
                      onClick={() => setCandidateDropdownOpen(!candidateDropdownOpen)}
                      aria-haspopup="true"
                      aria-expanded={candidateDropdownOpen}
                    >
                      For Candidates
                      <ChevronDownIcon className={`w-4 h-4 transition-transform ${candidateDropdownOpen ? "rotate-180" : ""}`} />
                    </button>
                    {candidateDropdownOpen && (
                      <div className="pl-4 flex flex-col space-y-1" role="menu">
                        <Link to="/candidate-how-it-works" className="block px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)} role="menuitem">How It Works</Link>
                        <Link to="/guides" className="block px-4 py-2 text-white hover:bg-deepblue" onClick={() => setMenuOpen(false)} role="menuitem">Guides</Link>
                      </div>
                    )}
                    {/* Standalone Pricing */}
                    <Link to="/pricing" className="px-4 py-2 text-white font-bold hover:bg-deepblue" onClick={() => setMenuOpen(false)}>Pricing</Link>
                  </div>
                )}
              </>
            )}

            {/* Right Side: Logout/Sign In Button */}
            <div className="flex items-center space-x-2">
                {isLoggedIn ? (
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-red text-white font-medium rounded hover:bg-deeporange"
                    >
                        Logout
                    </button>
                ) : (
                    <Link
                        to="/signin"
                        className="px-4 py-2 bg-green text-white font-medium rounded hover:bg-darkgreen"
                    >
                        Sign In
                    </Link>
                )}
            </div>

            {/* Invite Modal */}
            {isInviteModalOpen && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={closeInviteModal} // ✅ Close when clicking outside
                >
                    <div
                        className="bg-bluegray p-6 rounded-lg shadow-lg w-96"
                        onClick={(e) => e.stopPropagation()} // ✅ Prevent closing when clicking inside
                    >
                        <h2 className="text-2xl font-semibold text-orange mb-4">Invite a Friend</h2>

                        {/* Referral Link Section */}
                        {referralLink && (
                            <div className="bg-gray-800 p-3 rounded-lg shadow-md mb-4 flex flex-col items-center">
                                <span className="text-lightgray mb-2">Your Referral Link:</span>
                                <div className="flex items-center w-full">
                                    <input
                                        type="text"
                                        value={referralLink}
                                        readOnly
                                        className="bg-gray-700 text-white p-2 rounded-lg border border-gray-500 w-full"
                                    />
                                    <button onClick={() => navigator.clipboard.writeText(referralLink)} className="ml-2 p-2 bg-lightblue rounded-lg hover:bg-orange">
                                        <ClipboardDocumentIcon className="h-6 w-6 text-darkblue" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Subtle Divider */}
                        <div className="border-t border-gray-500 my-4"></div>

                        {/* Invite Form */}
                        <input
                            type="text"
                            name="firstName"
                            placeholder="First Name"
                            value={inviteData.firstName}
                            onChange={handleInviteChange}
                            className="w-full p-2 border border-deepblue rounded mb-3 focus:outline-none focus:ring-2 focus:ring-lightblue"
                        />
                        <input
                            type="text"
                            name="lastName"
                            placeholder="Last Name"
                            value={inviteData.lastName}
                            onChange={handleInviteChange}
                            className="w-full p-2 border border-deepblue rounded mb-3 focus:outline-none focus:ring-2 focus:ring-lightblue"
                        />
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={inviteData.email}
                            onChange={handleInviteChange}
                            className="w-full p-2 border border-deepblue rounded mb-3 focus:outline-none focus:ring-2 focus:ring-lightblue"
                        />

                        {/* Buttons */}
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={closeInviteModal}
                                className="px-4 py-2 border border-lightblue text-lightblue rounded hover:bg-deepblue transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={sendInvite}
                                className="px-4 py-2 bg-orange text-darkblue rounded hover:bg-deeporange transition"
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Header;
